import { api } from './client';

// ── V69 — Plan tier config (admin only) + public pricing ────────────────

export interface PlanTierFeatures {
  [key: string]: boolean;
}

export interface AdminPlanTier {
  planKey: string;
  displayName: string;
  tagline: string | null;
  // V162 — Dutch marketing copy for the bilingual public plans feed.
  displayNameNl: string | null;
  taglineNl: string | null;
  sortOrder: number;
  isPublic: boolean;
  priceEurMonthly: number;
  maxWorkspaces: number;
  maxBrandsPerWorkspace: number;
  maxPlatformsConnected: number;
  maxTeamMembers: number;
  maxApiKeys: number;
  maxPostsPerMonth: number;
  maxReelsPerMonthAudioOff: number;
  maxReelsPerMonthAudioOn: number;
  maxCarouselsPerMonth: number;
  maxImagesPerMonth: number;
  aiModelTier: string;
  /** V153 — per-workflow model overrides scoped to this tier. Map of
   *  `ai_model_<workflow>` (matching `AiModelResolver.Workflow.ALL`)
   *  → model id. Empty map = inherit per-workflow + global settings. */
  aiModelOverrides: Record<string, string>;
  features: PlanTierFeatures;
  updatedAt: string | null;
}

export interface FeatureCatalogEntry {
  key: string;
  displayName: string;
  description: string;
}

export interface AdminPlansResponse {
  tiers: AdminPlanTier[];
  featureCatalog: FeatureCatalogEntry[];
  updatedAt: string | null;
}

export interface PlanTierPatch {
  displayName?: string;
  tagline?: string | null;
  displayNameNl?: string | null;
  taglineNl?: string | null;
  sortOrder?: number;
  isPublic?: boolean;
  priceEurMonthly?: number;
  maxWorkspaces?: number;
  maxBrandsPerWorkspace?: number;
  maxPlatformsConnected?: number;
  maxTeamMembers?: number;
  maxApiKeys?: number;
  maxPostsPerMonth?: number;
  maxReelsPerMonthAudioOff?: number;
  maxReelsPerMonthAudioOn?: number;
  maxCarouselsPerMonth?: number;
  maxImagesPerMonth?: number;
  aiModelTier?: string;
  /** V153 — partial per-workflow override patch for this tier. Empty
   *  string value clears that workflow's override (inherits global).
   *  Send `{}` to clear all overrides. */
  aiModelOverrides?: Record<string, string>;
}

/** Live plan tier data from /api/public/plans — DB-backed, the source
 *  of truth admin edits in /admin/plans. BillingPage reads this so the
 *  prices shown to upgrading customers always match what admin set,
 *  not the hardcoded constants the page used to ship with. */
export interface PublicPlanTier {
  key: string;
  displayName: string;
  priceEurMonthly: number;
  priceEurAnnual: number;
}
export interface PublicPlansResponse {
  tiers: PublicPlanTier[];
  /** Admin-configurable trial length (system_settings.trial_duration_days).
   *  Defaults 14 on the backend. Used by Signup + Billing copy so the
   *  marketing text stays in sync with the actual workspace.trialEndsAt. */
  trialDurationDays?: number;
}
export const publicPlansApi = {
  list: () => api.get<PublicPlansResponse>('/api/public/plans').then((r) => r.data),
};

/**
 * V154 — upstream-provider health snapshot. Drives the
 * `ProviderStatusBanner` in DashboardLayout. Public (no JWT) so the
 * banner renders even on login / pricing pages. Backend key shape:
 * `anthropic:haiku` / `anthropic:sonnet` / `fal_ai` / etc.
 */
export interface ProviderStatusEntry {
  provider: string;
  status: 'ok' | 'degraded';
  reason: string | null;
  lastUpdatedAt: string;
}

export interface SystemStatusResponse {
  degraded: boolean;
  providers: ProviderStatusEntry[];
}

export const systemStatusApi = {
  status: () => api.get<SystemStatusResponse>('/api/public/system/status').then((r) => r.data),
};

export const adminPlansApi = {
  getAll: () => api.get<AdminPlansResponse>('/api/admin/plans').then((r) => r.data),
  patchTier: (planKey: string, patch: PlanTierPatch) =>
    api.patch<AdminPlanTier>(`/api/admin/plans/${planKey}`, patch).then((r) => r.data),
  patchFeature: (planKey: string, featureKey: string, enabled: boolean) =>
    api
      .patch<AdminPlanTier>(`/api/admin/plans/${planKey}/features/${featureKey}`, { enabled })
      .then((r) => r.data),
  // Step 1 — recommendations + warnings + per-tier cost estimates. Backed
  // by plan-recommendations.json on the backend; warnings are computed
  // fresh per call against the live plan_tiers cache.
  getAnalysis: () => api.get<PlanAnalysisResponse>('/api/admin/plans/analysis').then((r) => r.data),
};

// ── Plan analysis (recommendations + warnings + per-tier cost) ─────────

export interface PlanAnalysisResponse {
  schemaVersion: string;
  lastReviewed: string;
  recommendations: {
    features: FeatureRecommendation[];
    limits: LimitRecommendation[];
  };
  warnings: PlanWarning[];
  perTierEstimatedCost: Record<string, MonthlyAiCostBreakdown>;
  auditInputs: {
    defaultPostsPerWeekPerCadence: number;
    defaultPlatformsPerBrand: number;
  };
}

export interface FeatureRecommendation {
  key: string;
  displayName: string;
  headline: string;
  active: boolean;
  deprecated: boolean;
  recommendedTiers: string[];
  reasoning: string[];
}

export interface LimitRecommendation {
  field: string;
  displayName: string;
  perTier: Record<string, { value: number; reasoning: string }>;
}

export interface PlanWarning {
  code: string;
  /** HIGH | MEDIUM | LOW (server-emitted enum name) */
  severity: string;
  planKey: string;
  /** Feature key or limit field name; nullable for global warnings */
  targetKey: string | null;
  message: string;
}

export interface MonthlyAiCostBreakdown {
  postsEur: number;
  imagesEur: number;
  reelsAudioOffEur: number;
  reelsAudioOnEur: number;
  carouselsEur: number;
  totalEur: number;
  /** Null when the tier price is 0 (trial). */
  marginPct: number | null;
}

/**
 * Platform health snapshot + PromQL history proxy. Both endpoints live
 * under /api/admin/metrics so the admin-only authorization filter gates
 * them for free. history() returns [] when Prometheus isn't deployed.
 */
